import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { CartProvider } from "./store/CartContext";
import { LanguageProvider } from "./i18n/LanguageContext";
import { VIP_BOX_FEATURE_ENABLED } from "./config/features";

import StartPage from "./pages/StartPage";
import ApparelComingSoon from "./pages/ApparelComingSoon";
import DahliasHome from "./pages/DahliasHome";
import DahliasCatalog from "./pages/DahliasCatalog";
import DahliasMysteryBoxes from "./pages/DahliasMysteryBoxes";
import DahliasWorkshops from "./pages/DahliasWorkshops";
import PrivatePickings from "./pages/PrivatePickings";
import DahliasAbout from "./pages/DahliasAbout";
import BloomClubExternalRedirect from "./pages/BloomClubExternalRedirect";
import BreedersGarden from "./pages/BreedersGarden";
import AdminSeedlings from "./pages/admin/AdminSeedlings";

import ShopPage from "./pages/ShopPage";
import ProductDetailPage from "./pages/ProductDetailPage";
import CartPage from "./pages/CartPage";
import CheckoutPage from "./pages/CheckoutPage";
import OrderSuccessPage from "./pages/OrderSuccessPage";

import AdminLogin from "./pages/admin/AdminLogin";

import GoogleAuthCallback from "./pages/GoogleAuthCallback";

// Google sign-in returns to /account#session_id=… ; handle it before any route renders.
function GoogleCallbackGate({ children }) {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <GoogleAuthCallback />;
  return children;
}

function LegacyOrderRedirect() {
  const { orderId } = useParams();
  return <Navigate to={`/admin/order/${orderId}`} replace />;
}
// Legacy AdminCustomerMessages removed in V2 reset (replaced by new /admin/admin/AdminCustomerMessages)
// Admin Panel rebuild , Feb 2026
import AdminPanelLayout from "./admin/AdminPanelLayout";
import AdminTuberAudit from "./admin/AdminTuberAudit";
import AdminPullList from "./admin/AdminPullList";
import AdminPrivateBookings from "./admin/AdminPrivateBookings";
import AdminPriorityWorkspace from "./admin/AdminPriorityWorkspace";
import AdminMassEmail from "./admin/AdminMassEmail";
import AdminDelivery from "./admin/AdminDelivery";
import AdminAvailableToSell from "./admin/AdminAvailableToSell";
import AdminLabelMaker from "./admin/AdminLabelMaker";
import AdminHistoric from "./admin/AdminHistoric";
import AdminHub from "./admin/AdminHub";
import AdminHubSection from "./admin/AdminHubSection";
import AdminPayments from "./admin/AdminPayments";
import AdminBumface from "./admin/AdminBumface";
import AdminBankAuditQueue from "./admin/AdminBankAuditQueue";
import AdminFinance from "./admin/finance/AdminFinance";
import AdminCustomerAudit from "./admin/AdminCustomerAudit";
import AdminStaff from "./admin/AdminStaff";
import AdminCompletedHistorical from "./admin/AdminCompletedHistorical";
import AdminFairsale from "./admin/AdminFairsale";
import AdminPrioritisedCustomers from "./admin/AdminPrioritisedCustomers";
import AdminResetSystem from "./admin/AdminResetSystem";
import AdminWishlistHeatmap from "./admin/AdminWishlistHeatmap";
import AdminBulkImport from "./admin/AdminBulkImport";
// Legacy AdminCommunications removed in V2 reset (replaced by new /admin/AdminCommunications)

// Console (new admin v2, /console/*)
import ConsoleLayout from "./console/ConsoleLayout";
import ConsoleDashboard from "./console/ConsoleDashboard";
import ConsoleCreateOrder from "./console/ConsoleCreateOrder";
import ConsoleOrdersList from "./console/ConsoleOrdersList";
import ConsoleArchivedOrders from "./console/ConsoleArchivedOrders";
import ConsoleOrderDetail from "./console/ConsoleOrderDetail";
import ConsoleCustomers from "./console/ConsoleCustomers";
import ConsoleComplaintForm from "./console/ConsoleComplaintForm";
import ConsoleInventory from "./console/ConsoleInventory";
import ConsoleMessages from "./console/ConsoleMessages";
import ConsoleTestHq from "./console/ConsoleTestHq";
// Resend-era components removed Feb 24 2026 (Phase 17 cleanup)
// Ported from old /admin/* (Jun 2026 audit + consolidation)
import ConsoleReports from "./console/ConsoleReports";
import ConsoleJobs from "./console/ConsoleJobs";
import ConsoleDeliveryRoutes from "./console/ConsoleDeliveryRoutes";
import ConsoleCatalog from "./console/ConsoleCatalog";
import ConsoleProducts from "./console/ConsoleProducts";
import ConsoleVouchers from "./console/ConsoleVouchers";
// ConsoleMassEmail removed Feb 24 2026 , Resend-era component
import ConsoleSiteContent from "./console/ConsoleSiteContent";
import ConsoleSiteBuilder from "./console/ConsoleSiteBuilder";
// ConsoleVipBoxes import removed Jun 27 2026 , no longer routed (replaced by HQ VipBoxBuilder)
import ConsoleResetPassword from "./console/ConsoleResetPassword";
import VipBoxBuilder from "./pages/VipBoxBuilder";
import LegalPage from "./pages/LegalPage";
import CustomerPortal, { CustomerLogin, CustomerSignup } from "./pages/CustomerPortal";
import HQLoginLink from "./hq/HQLoginLink";
import FairitagePage from "./pages/FairitagePage";
import HQFairSale from "./hq/HQFairSale";
import ManagementPortal, { ManagementLogin } from "./pages/ManagementPortal";
import SubscriptionTerms from "./pages/legal/SubscriptionTerms";
import PrivacyPolicy from "./pages/legal/PrivacyPolicy";
import TermsOfUse from "./pages/legal/TermsOfUse";
import HeritageGarden from "./pages/HeritageGarden";
// New Heritage HQ (Phase 1 rebuild , Jun 2026)
import HQLayout from "./hq/HQLayout";
import HQDashboard from "./hq/HQDashboard";
import HQComingSoon from "./hq/HQComingSoon";
import HQContactHeritage from "./hq/HQContactHeritage";
import HQOrders from "./hq/HQOrders";
import HQOrderDetail from "./hq/HQOrderDetail";
import HQProfile from "./hq/HQProfile";
import HQOverview from "./hq/HQOverview";
import HQCredits from "./hq/HQCredits";
import HQStatements from "./hq/HQStatements";
import HQCatalog from "./hq/HQCatalog";
import HQMyPicks from "./hq/HQMyPicks";
import HQCrushes from "./hq/HQCrushes";
import CuttingCrushClub from "./pages/CuttingCrushClub";
import OpenDayPage from "./components/visit/OpenDays";
import AdminVisit from "./admin/AdminVisit";
import AdminOpenDays from "./admin/AdminOpenDays";
import OpenDayTicketPage from "./pages/OpenDayTicketPage";
import AdminStockReconcile from "./admin/AdminStockReconcile";
import AdminToday from "./admin/AdminToday";
import AdminPrintCenter from "./admin/AdminPrintCenter";
import AdminOrdersBoard from "./admin/AdminOrdersBoard";
import LegacyOrdersRedirect from "./admin/LegacyOrdersRedirect";
import AdminHolds from "./admin/AdminHolds";
import SummerCalendar from "./pages/SummerCalendar";
import SalesGate from "./components/dahlias/SalesGate";
import HQTickets from "./hq/HQTickets";
import AdminCuttingRequests from "./admin/AdminCuttingRequests";
import HQSelfAudit from "./hq/HQSelfAudit";
import HQPrivateBookings from "./hq/HQPrivateBookings";
import HQPriorityOrder from "./hq/HQPriorityOrder";
import { CustomerForgotPassword, CustomerResetPassword } from "./pages/CustomerPasswordPages";
import RequireAdmin from "./pages/admin/RequireAdmin";
import ScrollToTop from "./components/ScrollToTop";
import LaunchBanner from "./components/LaunchBanner";

/* Render the launch banner only on public-facing routes, not inside the
   admin/console/account portals where it would just be in the way. */
function PublicBannerGate() {
  const path = (typeof window !== "undefined" ? window.location.pathname : "/") || "/";
  const isInternal = path.startsWith("/admin") || path.startsWith("/console") || path.startsWith("/account");
  if (isInternal) return null;
  return <LaunchBanner />;
}

function App() {
  return (
    <div className="App">
      <LanguageProvider>
      <CartProvider>
        <BrowserRouter>
          <ScrollToTop />
          <PublicBannerGate />
          <GoogleCallbackGate>
          <Routes>
            <Route path="/" element={<StartPage />} />
            <Route path="/apparel" element={<ApparelComingSoon />} />

            <Route path="/dahlias" element={<DahliasHome />} />
            <Route path="/dahlias/store" element={<SalesGate area="store"><ShopPage /></SalesGate>} />
            <Route path="/dahlias/catalog" element={<SalesGate area="catalogue"><DahliasCatalog /></SalesGate>} />
            <Route path="/crush" element={<CuttingCrushClub />} />
            <Route path="/dahlias/mystery-boxes" element={<DahliasMysteryBoxes />} />
            <Route path="/dahlias/tubers" element={<Navigate to="/dahlias/store" replace />} />
            <Route path="/dahlias/workshops" element={<DahliasWorkshops />} />
            <Route path="/dahlias/private-pickings" element={<PrivatePickings />} />
            <Route path="/private-pickings" element={<Navigate to="/dahlias/private-pickings" replace />} />
            <Route path="/visit" element={<Navigate to="/dahlias/private-pickings" replace />} />
            <Route path="/visit/open-days/:slug" element={<OpenDayPage />} />
            <Route path="/visit/tickets/:orderId" element={<OpenDayTicketPage />} />
            <Route path="/visit/calendar" element={<SummerCalendar />} />
            <Route path="/dahlias/app" element={<Navigate to="/bloom-keeper" replace />} />
            <Route path="/dahlias/about" element={<DahliasAbout />} />
            <Route path="/dahlias/contact" element={<Navigate to="/dahlias/about" replace />} />
            <Route path="/dahlias/farm" element={<Navigate to="/dahlias/about" replace />} />

            <Route path="/breeders-garden" element={<BreedersGarden />} />
            <Route path="/heritage-garden" element={<HeritageGarden />} />
            <Route path="/account/breeders-club" element={<Navigate to="/breeders-garden" replace />} />

            <Route path="/bloom-keeper" element={<BloomClubExternalRedirect />} />
            <Route path="/bloom-keeper/app" element={<BloomClubExternalRedirect />} />
            <Route path="/bloom-box" element={<BloomClubExternalRedirect />} />
            <Route path="/bloom-club" element={<BloomClubExternalRedirect />} />
            <Route path="/bloom-club/growers" element={<BloomClubExternalRedirect />} />
            <Route path="/bloom-club/territories" element={<BloomClubExternalRedirect />} />
            <Route path="/bloom-club/join" element={<BloomClubExternalRedirect />} />
            <Route path="/bloom-club/join/success" element={<BloomClubExternalRedirect />} />
            <Route path="/bloom-club/grower/apply" element={<BloomClubExternalRedirect />} />
            <Route path="/bloom-club/grower/track" element={<BloomClubExternalRedirect />} />
            <Route path="/bloom-club/grower/set-password" element={<BloomClubExternalRedirect />} />
            <Route path="/bloom-club/grower/login" element={<BloomClubExternalRedirect />} />
            <Route path="/bloom-club/grower" element={<BloomClubExternalRedirect />} />
            <Route path="/bloom-club/prospectus/*" element={<BloomClubExternalRedirect />} />
            <Route path="/grow-with-us" element={<BloomClubExternalRedirect />} />
            <Route path="/legal/subscription-terms" element={<SubscriptionTerms />} />
            <Route path="/legal/terms-of-use" element={<TermsOfUse />} />
            <Route path="/legal/privacy" element={<PrivacyPolicy />} />

            <Route path="/shop" element={<Navigate to="/dahlias/store" replace />} />
            <Route path="/shop/:slug" element={<SalesGate area="store"><ProductDetailPage /></SalesGate>} />
            <Route path="/cart" element={<SalesGate area="checkout"><CartPage /></SalesGate>} />
            <Route path="/checkout" element={<SalesGate area="checkout"><CheckoutPage /></SalesGate>} />
            <Route path="/order/:orderId" element={<OrderSuccessPage />} />

            {/* Heritage Customer Portal */}
            <Route path="/account/signup" element={<CustomerSignup />} />
            <Route path="/fairitage" element={<FairitagePage />} />
            <Route path="/account/login" element={<CustomerLogin />} />
            <Route path="/account/forgot-password" element={<CustomerForgotPassword />} />
            <Route path="/account/reset-password" element={<CustomerResetPassword />} />
            <Route path="/account/link" element={<HQLoginLink />} />
            {/* Customer HQ (Feb 2026 simplified rebuild) , single layout, six tiles,
                no jumping between portals. CustomerPortal/legacy routes removed. */}
            <Route path="/account" element={<HQLayout />}>
              <Route index element={<HQDashboard />} />
              <Route path="dashboard"  element={<HQDashboard />} />
              <Route path="overview"   element={<HQOverview />} />
              <Route path="self-audit" element={<HQSelfAudit />} />
              <Route path="private-bookings" element={<HQPrivateBookings />} />
              <Route path="priority-order" element={<HQPriorityOrder />} />
              <Route path="orders"     element={<HQOrders />} />
              <Route path="fairsale"   element={<HQFairSale />} />
              <Route path="orders/:orderId" element={<HQOrderDetail />} />
              <Route path="details"    element={<HQProfile />} />
              <Route path="credits"    element={<HQCredits />} />
              <Route path="statements" element={<HQStatements />} />
              <Route path="catalog"    element={<HQCatalog />} />
              <Route path="contact"    element={<HQContactHeritage />} />
              <Route path="my-picks"   element={<HQMyPicks />} />
              <Route path="crushes"    element={<HQCrushes />} />
              <Route path="tickets"    element={<HQTickets />} />
              <Route path="help"       element={<HQComingSoon title="Help Centre" />} />
              <Route path="grower-portal"      element={<BloomClubExternalRedirect />} />
              <Route path="grower-portal/:tab" element={<BloomClubExternalRedirect />} />
              <Route path="subscriber-portal"  element={<BloomClubExternalRedirect />} />
              <Route path="subscriber-portal/:tab" element={<BloomClubExternalRedirect />} />
              <Route path="*" element={<Navigate to="/account" replace />} />
            </Route>
            <Route path="/legal/:slug" element={<LegalPage />} />
            <Route path="/legal" element={<LegalPage />} />

            {/* Heritage Management Portal (hidden) */}
            <Route path="/management/login" element={<ManagementLogin />} />
            <Route path="/management" element={<ManagementPortal />} />

            <Route path="/admin/login" element={<AdminLogin />} />

            {/* Admin Panel , Feb 2026 reset rebuild. Simple 14-tab interface.
                Customers / Create Order / Order pipeline / Stock / Financials
                / Stripe / Complaints / Vouchers / Content / Test / Reset /
                + temporary Historic Migration tab. Operational fulfilment stays
                in the Management Portal at /console. */}
            <Route path="/admin" element={<RequireAdmin><AdminPanelLayout /></RequireAdmin>}>
              {/* Default landing , ADHD-friendly hub dashboard */}
              <Route index element={<AdminToday />} />
              <Route path="hub" element={<AdminHub />} />
              <Route path="hub/:section" element={<AdminHubSection />} />
              <Route path="payments" element={<AdminPayments />} />
              <Route path="customer-audit" element={<AdminCustomerAudit />} />
              <Route path="bumface" element={<AdminBumface />} />
              <Route path="bank-auditor-queue" element={<AdminBankAuditQueue />} />
              <Route path="messages" element={<ConsoleMessages />} />
              <Route path="staff" element={<AdminStaff />} />
              <Route path="seedlings" element={<AdminSeedlings />} />
              <Route path="fairsale" element={<AdminFairsale />} />

              {/* Orders */}
              <Route path="orders/create"           element={<ConsoleCreateOrder />} />
              <Route path="orders" element={<AdminOrdersBoard />} />
              <Route path="orders/awaiting-payment" element={<LegacyOrdersRedirect filter="attention" />} />
              <Route path="orders/all" element={<LegacyOrdersRedirect filter="all" />} />
              <Route path="orders/legacy/awaiting-payment" element={<ConsoleOrdersList stage="awaiting_payment" title="Waiting for payment" />} />
              <Route path="orders/legacy/all" element={<ConsoleOrdersList stage="all" />} />
              <Route path="orders/legacy/packing" element={<ConsoleOrdersList stage="assigned" />} />
              <Route path="orders/legacy/to-ship" element={<ConsoleOrdersList stage="shipping" />} />
              <Route path="orders/legacy/completed" element={<ConsoleOrdersList stage="completed" />} />
              <Route path="order/:orderId" element={<ConsoleOrderDetail />} />
              <Route path="orders/packing"          element={<LegacyOrdersRedirect filter="to_pack" />} />
              <Route path="orders/to-ship"          element={<LegacyOrdersRedirect filter="sent" />} />
              <Route path="orders/completed"        element={<LegacyOrdersRedirect filter="complete" />} />
              <Route path="orders/archived"         element={<ConsoleArchivedOrders />} />
              <Route path="tuber-audit" element={<AdminTuberAudit />} />
              <Route path="available-to-sell" element={<AdminAvailableToSell />} />
              <Route path="pull-list"   element={<AdminPullList />} />
              <Route path="print"       element={<AdminPrintCenter />} />
              <Route path="holds"       element={<AdminHolds />} />
              <Route path="private-bookings" element={<AdminPrivateBookings />} />
              <Route path="cutting-requests" element={<AdminCuttingRequests />} />
              <Route path="visit" element={<AdminVisit />} />
              <Route path="open-days" element={<AdminOpenDays />} />
              <Route path="stock-reconcile" element={<AdminStockReconcile />} />
              <Route path="mass-email" element={<AdminMassEmail />} />
              <Route path="delivery" element={<AdminDelivery />} />
              <Route path="label-maker" element={<AdminLabelMaker />} />

              {/* Operations */}
              <Route path="catalog"     element={<ConsoleCatalog />} />
              <Route path="boxes"       element={<ConsoleProducts kind="boxes" />} />
              <Route path="store-products" element={<ConsoleProducts kind="all" />} />
              <Route path="stock"       element={<ConsoleInventory />} />
              <Route path="finance" element={<AdminFinance />} />
              <Route path="financials"  element={<Navigate to="/admin/finance" replace />} />
              <Route path="financial-summary" element={<Navigate to="/admin/finance" replace />} />
              <Route path="financial-overview" element={<Navigate to="/admin/finance?tab=bank" replace />} />
              <Route path="vouchers"    element={<ConsoleVouchers />} />

              {/* Customers extras */}
              <Route path="orders/historical"  element={<AdminHistoric />} />
              <Route path="orders/completed-historical" element={<AdminCompletedHistorical />} />
              <Route path="orders/complaints"  element={<ConsoleOrdersList stage="complaint" title="Complaint / Refund / Replace" />} />
              <Route path="customers/priority" element={<AdminPrioritisedCustomers />} />
              <Route path="customers/priority/:customerId" element={<AdminPriorityWorkspace />} />
              <Route path="customers/wishlist-heatmap" element={<AdminWishlistHeatmap />} />
              <Route path="bulk-import" element={<AdminBulkImport />} />

              {/* System */}
              <Route path="content"         element={<ConsoleSiteContent />} />
              <Route path="site-builder"    element={<ConsoleSiteBuilder />} />
              <Route path="test-accounts"   element={<ConsoleTestHq />} />
              <Route path="reset-password"  element={<ConsoleResetPassword />} />

              {/* Temporary historic migration tab */}
              <Route path="historic" element={<AdminHistoric />} />
              <Route path="system/reset" element={<AdminResetSystem />} />

              {/* Legacy bookmarks redirect to closest new tab */}
              <Route path="customers"          element={<ConsoleCustomers />} />
              <Route path="orders"             element={<Navigate to="/admin/orders/awaiting-payment" replace />} />
              <Route path="orders/:orderId"    element={<LegacyOrderRedirect />} />
              <Route path="orders/awaiting-inventory" element={<Navigate to="/admin/orders/packing" replace />} />
              <Route path="orders/assigned"    element={<Navigate to="/admin/orders/packing" replace />} />
              <Route path="orders/finalised"   element={<Navigate to="/admin/orders/packing" replace />} />
              <Route path="orders/shipping"    element={<Navigate to="/admin/orders/to-ship" replace />} />

              {/* Any other legacy /admin/* paths go to the Customers tab */}
              <Route path="*" element={<Navigate to="/admin" replace />} />
            </Route>

            {/* Console , Management Portal (operational only) , strictly
                separated from /admin. Tabs: Dashboard, Financial Summary,
                Awaiting Payment, Packing, Shipping, Quality Control,
                Completed, Monthly Reports. All admin-only tools (catalog,
                vouchers, content, customers admin) live in /admin only. */}
            <Route path="/console" element={<RequireAdmin><ConsoleLayout /></RequireAdmin>}>
              <Route index element={<ConsoleDashboard />} />
              <Route path="financial-summary" element={<Navigate to="/admin/finance" replace />} />
              <Route path="orders/awaiting_payment" element={<ConsoleOrdersList stage="awaiting_payment" />} />
              <Route path="orders/packing" element={<ConsoleOrdersList stage="assigned" />} />
              <Route path="orders/shipping" element={<ConsoleOrdersList stage="shipping" />} />
              <Route path="orders/qc" element={<ConsoleOrdersList stage="quality_control" />} />
              <Route path="orders/completed" element={<ConsoleOrdersList stage="completed" />} />
              <Route path="orders/:orderId/complaint" element={<ConsoleComplaintForm />} />
              <Route path="reports" element={<ConsoleReports />} />
              <Route path="jobs" element={<ConsoleJobs />} />
              <Route path="delivery-routes" element={<ConsoleDeliveryRoutes />} />
              {/* Legacy bookmarks: route everything else back to the dashboard. */}
              <Route path="orders/assigned" element={<Navigate to="/console/orders/packing" replace />} />
              <Route path="orders/awaiting_inventory" element={<Navigate to="/console/orders/packing" replace />} />
              <Route path="orders/finalised" element={<Navigate to="/console/orders/packing" replace />} />
              <Route path="orders/complaints" element={<ConsoleOrdersList stage="complaint" />} />
              <Route path="*" element={<Navigate to="/console" replace />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </GoogleCallbackGate>
        </BrowserRouter>
        <Toaster richColors position="top-right" />
      </CartProvider>
      </LanguageProvider>
    </div>
  );
}

export default App;
